import { ChevronDown, ChevronRight, HelpCircle } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { NavLink, useParams } from "react-router-dom";
import type { Article, Category } from "@core/features/support";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";

export interface Props {
  category: Category;
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
  onArticleClick?: (article: Article) => void;
  onCategoryClick: (category: Category) => void;
}

export function SupportCategoryTreeCategoryItem({
  category,
  depth,
  hasChildren,
  isExpanded,
  onArticleClick,
  onCategoryClick,
}: Props) {
  const { id: programId } = useParams<{ id: string }>();
  if (category.articles.length === 0 && category.subcategories.length === 0) {
    return null;
  }

  return (
    <li className="mb-1">
      <div
        className={twMerge(
          "flex items-center py-1.5 px-2 rounded-md text-primary-800 hover:bg-brand-100 cursor-pointer",
          depth > 0 && `ml-${depth * 1}`
        )}
        onClick={() => onCategoryClick(category)}
      >
        {hasChildren && (
          <span className="mr-1 text-primary-700 flex-shrink-0">
            {isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </span>
        )}
        <Type variant="h5">{category.name}</Type>
      </div>

      {isExpanded && hasChildren && (
        <ul className="mt-1 space-y-0.5">
          {category.subcategories.map((subcategory) => (
            <SupportCategoryTreeCategoryItem
              key={subcategory.id}
              category={subcategory}
              depth={depth + 1}
              hasChildren={
                subcategory.subcategories.length > 0 ||
                subcategory.articles.length > 0
              }
              isExpanded={isExpanded}
              onArticleClick={onArticleClick}
              onCategoryClick={onCategoryClick}
            />
          ))}
          {category.articles.map((article) => (
            <li key={article.id} className={twMerge(`ml-${(depth + 2) * 1}`)}>
              <NavLink
                to={`/program/${programId}/support/${article.id}`}
                onClick={(e) => {
                  if (onArticleClick) {
                    e.preventDefault();
                    onArticleClick(article);
                  }
                }}
              >
                {({ isActive }) => (
                  <Button
                    variant="ghost"
                    className={twMerge(
                      isActive
                        ? "bg-brand-200 text-primary-800"
                        : "text-primary-700 hover:bg-brand-100"
                    )}
                    typeClassName="max-w-40 truncate"
                    iconComponent={HelpCircle}
                    data-testid={`support-category-article-${article.id}`}
                  >
                    {article.title}
                  </Button>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default SupportCategoryTreeCategoryItem;

